import { useCallback, useEffect, useState } from "react";
const URL_PARAM = "g";
/**
 * Window event that opens the launcher — the touch-friendly path for devices
 * with no Cmd/Ctrl+G (phones/tablets). A shell can render a search button that
 * calls `openUniversalLauncher()`; every mounted `<UniversalLauncher>` listens.
 */
export const LAUNCHER_OPEN_EVENT = "g1:launcher:open";
/** Open the universal launcher imperatively (e.g. from a topbar search button). */
export function openUniversalLauncher() {
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(LAUNCHER_OPEN_EVENT));
    }
}
function readInitialFromUrl() {
    if (typeof window === "undefined")
        return { open: false, query: "" };
    try {
        const url = new URL(window.location.href);
        const raw = url.searchParams.get(URL_PARAM);
        if (raw === null)
            return { open: false, query: "" };
        return { open: true, query: raw };
    }
    catch {
        return { open: false, query: "" };
    }
}
/**
 * Wires the global Cmd/Ctrl+G shortcut for the universal launcher (apps +
 * search + recent in one modal). Esc closes. Returns open state plus
 * imperative open/close so app-specific menu items can trigger it too.
 *
 * Reads `?g=<query>` from the URL on mount — shareable launches like
 * https://review.g1.network/?g=email open the launcher prefilled. State
 * is mirrored back to the URL via history.replaceState (no navigation).
 *
 * Cmd+G overrides the browser's "Find Next" — that's a deliberate trade so
 * the operator console gets a single muscle-memory shortcut for everything.
 */
export function useUniversalLauncher() {
    const [{ open, query: initialQuery }] = useState(readInitialFromUrl);
    const [openState, setOpen] = useState(open);
    const openLauncher = useCallback(() => setOpen(true), []);
    const closeLauncher = useCallback(() => setOpen(false), []);
    const toggleLauncher = useCallback(() => setOpen((v) => !v), []);
    useEffect(() => {
        const handler = (e) => {
            const isCmdG = (e.metaKey || e.ctrlKey) && (e.key === "g" || e.key === "G");
            if (isCmdG) {
                e.preventDefault();
                setOpen((v) => !v);
                return;
            }
            if (e.key === "Escape")
                setOpen(false);
        };
        const openHandler = () => setOpen(true);
        window.addEventListener("keydown", handler);
        window.addEventListener(LAUNCHER_OPEN_EVENT, openHandler);
        return () => {
            window.removeEventListener("keydown", handler);
            window.removeEventListener(LAUNCHER_OPEN_EVENT, openHandler);
        };
    }, []);
    return { open: openState, openLauncher, closeLauncher, toggleLauncher, initialQuery };
}
//# sourceMappingURL=useUniversalLauncher.js.map